export type ExportVariant = "stock" | "debug";

export type SplitExport = {
  variant: ExportVariant;
  preambleText: string | undefined;
  battleLogText: string;
  /** `[Tag] ...` lines the modified client appends after the battle log (e.g. `[CompetitiveElo]`). */
  trailerLines: string[];
};

const TAGGED_LINE = /^\[[A-Za-z0-9_-]+\]\s?/;

/** Split trailing `[Tag]` lines (post-match additions) off the battle-log body. */
function splitTrailer(body: string): { battleLogText: string; trailerLines: string[] } {
  const lines = body.split("\n");
  const trailerLines: string[] = [];
  let end = lines.length;
  while (end > 0) {
    const line = (lines[end - 1] ?? "").trim();
    if (line.length === 0) {
      end -= 1;
      continue;
    }
    if (!TAGGED_LINE.test(line)) break;
    trailerLines.unshift(line);
    end -= 1;
  }
  return { battleLogText: lines.slice(0, end).join("\n").trim(), trailerLines };
}

const BATTLE_LOG_MARKER = /^=== Battle Log ===\s*$/m;
const MATCH_START_MARKER = /^=== Match Start ===\s*$/m;

function normalizeNewlines(text: string): string {
  return text.replace(/^﻿/, "").replace(/\r\n?/g, "\n");
}

/**
 * Separate the debug preamble (modified client) from the stock battle-log export.
 * Stock exports have no markers and are returned whole as the battle log.
 */
export function splitExport(rawText: string): SplitExport {
  const text = normalizeNewlines(rawText);
  const marker = BATTLE_LOG_MARKER.exec(text);
  if (marker === null) {
    const { battleLogText, trailerLines } = splitTrailer(text);
    return {
      variant: trailerLines.length > 0 ? "debug" : "stock",
      preambleText: undefined,
      battleLogText,
      trailerLines,
    };
  }
  let preamble = text.slice(0, marker.index);
  const start = MATCH_START_MARKER.exec(preamble);
  if (start !== null) {
    preamble = preamble.slice(start.index + start[0].length);
  }
  const { battleLogText, trailerLines } = splitTrailer(text.slice(marker.index + marker[0].length));
  return { variant: "debug", preambleText: preamble.trim(), battleLogText, trailerLines };
}

/** Canonical form of the battle-log section used for content hashing / dedupe. */
export function normalizeBattleLog(battleLogText: string): string {
  return normalizeNewlines(battleLogText)
    .split("\n")
    .map((line) => line.replace(/\s+$/, ""))
    .filter((line) => line.length > 0)
    .join("\n");
}

/** Cheap gate before parsing: does this look like a TCG Live battle log at all? */
export function looksLikeBattleLog(text: string): boolean {
  const body = splitExport(text).battleLogText;
  const hasSetup = /^Setup\s*$/m.test(body);
  const hasTurn = /^(?:Turn # \d+ - )?.+['’]s Turn\s*$/m.test(body);
  const hasOpening = /drew \d+ cards? for the opening hand\./.test(body);
  return hasSetup && (hasTurn || hasOpening);
}
